import {
  defineScenario,
  act,
  beat,
  gp,
  infra,
  PACING,
  establishingWide,
  dronePushIn,
  orbit180,
  craneReveal,
  majesticPullback,
  hudEvent,
  hudTeam,
  hudRegion,
  notification,
  productScreen,
  panel,
  updatePanel,
  removePanel,
  dimStart,
  dimEnd,
  hold,
} from '../authoring';
import type { ScenarioStep } from '../../core/types';
import { assetUrl } from '../../../lib/assets';

// ──────────────────────────────────────────────────────────────────────────
// SCENARIO — CAPITAL INVESTMENT CONTROLS
//
// End-to-end Capital Delivery work management and Control Room.
// Two projects in portfolio: one ON TRACK (green), one IN EXCEPTION (red).
// The story follows the exception project through schedule slippage,
// investment oversight, and AI-driven recovery — purely financial/governance.
//
// UNIQUE VISUAL DNA:
// 1. WIDEST zoom — city-scale portfolio view (zoom 13-14), executive level
// 2. multi-site-rag with 8+ sites — the largest constellation in any scenario
// 3. model-status on MULTIPLE infra simultaneously (portfolio health glow)
// 4. flow-pulse as BUDGET FLOW between sites and a central hub
// 5. NO comms-broadcast, NO excavation, NO vehicles, NO pipe-lay
// 6. Colour grade: corporate blue → red alert → resolved green
// 7. Budget burndown hud-metric as primary tension (£ filling red)
//
// The drama is governance: a RED project drags the portfolio, AI reforecasts,
// recovery actions close it out, confidence restores.
// ──────────────────────────────────────────────────────────────────────────

// ─── Portfolio sites (spread across the full city) ──────────────────────
const HUB = infra('control-centre', gp(-2, -4));

// On-track project: "East West Rail — Phase 4"
const PROJ_GREEN = infra('sub-south', gp(11, 22));

// In-exception project: "Newgate Lane — Deadham"
const PROJ_RED = infra('depot-east', gp(14, -16));


// Budget flow paths (hub → project sites)
const BUDGET_TO_GREEN: [number, number][] = [
  gp(-2, -4), gp(2, 4), gp(6, 10), gp(9, 16), gp(11, 22),
];
const BUDGET_TO_RED: [number, number][] = [
  gp(-2, -4), gp(4, -8), gp(8, -12), gp(12, -14), gp(14, -16),
];

export const capitalInvestmentScenario = defineScenario(
  {
    id: 'capital-investment',
    title: 'Optimal Management of AMP8, GD3 and ED3 Capital Programmes',
    description: 'Portfolio oversight — two projects, one in exception. Schedule slippage, budget variance, AI recovery.',
    hudLabel: 'CAPITAL DELIVERY',
    telemetry: 'capital-investment',
    activeInfraIds: [
      'control-centre', 'sub-south', 'depot-east',
      'pump-beta', 'cooling-a', 'tower-north',
      'water-treatment', 'depot-west', 'tower-south',
    ],
  },
  [
    // ═══════════════════════════════════════════════════════════════════════
    // ACT 1: EXEC VIEW — Capital Delivery Live (0–25s)
    // Widest zoom. Full portfolio constellation visible. Corporate blue grade.
    // 625 total projects, £42M programme. Two focal projects introduced.
    // ═══════════════════════════════════════════════════════════════════════
    act('Exec View', [
      dimStart,
      { type: 'color-grade', duration: 0, params: { tint: '#1e40af', intensity: 0.06, vignette: 0.15 } } as ScenarioStep,

      // Full portfolio constellation — 8 sites all green/amber initially
      beat(PACING.ESTABLISH, [
        establishingWide(HUB, { bearing: -15, pitch: 42, zoom: 13.5 }),
        { type: 'multi-site-rag', duration: 0, params: {
          id: 'portfolio',
          sites: [
            { coordinates: gp(11, 22), status: 'green' },
            { coordinates: gp(14, -16), status: 'green' },
            { coordinates: gp(-25, -12), status: 'green' },
            { coordinates: gp(22, 22), status: 'green' },
            { coordinates: gp(26, -24), status: 'amber' },
            { coordinates: gp(-22, -22), status: 'green' },
            { coordinates: gp(-26, 6), status: 'green' },
            { coordinates: gp(-18, 28), status: 'amber' },
          ],
          linkTo: { coordinates: gp(-2, -4), infraId: 'control-centre' },
        }},
        { type: 'model-status', duration: 0, params: { coordinates: gp(-2, -4), infraId: 'control-centre', color: '#0ea5e9', emissive: 0.5 } },
        hudEvent('programme', 'CAPITAL DELIVERY LIVE', '625 projects — £42M programme value. Portfolio health: 6.5% avg progress.', 'info'),
        hudRegion([
          { name: 'On Track', status: 'green', properties: 583 },
          { name: 'At Risk', status: 'amber', properties: 34 },
          { name: 'In Exception', status: 'red', properties: 8 },
        ]),
        { type: 'hud-metric', duration: 0, params: { id: 'budget', kind: 'burndown', label: 'Programme Spend', value: 12, target: 42, unit: '£M', severity: 'info' } },
        { type: 'hud-metric', duration: 0, params: { id: 'confidence', kind: 'counter', label: 'Confidence', value: 87, unit: '%', severity: 'success' } },
      ]),

      // Budget flow lines show money moving from hub to projects
      beat(PACING.PUSH_IN, [
        { type: 'camera', duration: PACING.PUSH_IN, params: { center: gp(-2, -4), infraId: 'control-centre', zoom: 14, pitch: 45, bearing: 10 } },
        { type: 'flow-pulse', duration: PACING.PUSH_IN, params: {
          id: 'budget-flow-green', path: BUDGET_TO_GREEN, color: '#10b981', speed: 0.8,
        }},
        { type: 'flow-pulse', duration: PACING.PUSH_IN, params: {
          id: 'budget-flow-red', path: BUDGET_TO_RED, color: '#10b981', speed: 0.8,
        }},
        hudEvent('finance', 'INVESTMENT POSITION', 'Spent: £12M of £42M. Remaining: £30M. 6 months to AMP close.', 'info'),
        notification('PORTFOLIO OVERVIEW', '625 projects across 8 programme areas. Live investment tracking.', 'info', PACING.BEAT),
      ]),

      productScreen('Control Room — Capital Delivery Live', {
        image: assetUrl('/screens/capd-portfolio-manager.png'),
        device: 'desktop',
        subtitle: 'Portfolio Manager — all active projects with RAG status',
        role: 'Programme Director',
        url: 'app.skewb.io/capital-delivery/live',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 2: PROJECT CARDS — Two projects, one green one red (25–55s)
    // Camera moves between the two focal projects. Green project is healthy.
    // Red project flashes to exception status. Constellation updates.
    // ═══════════════════════════════════════════════════════════════════════
    act('Project Cards', [
      // Green project — East West Rail Phase 4
      beat(7000, [
        dronePushIn(PROJ_GREEN, { bearing: -20, zoom: 15.5, duration: 7000 }),
        { type: 'model-status', duration: 0, params: { coordinates: gp(11, 22), infraId: 'sub-south', color: '#10b981', emissive: 0.6 } },
        panel('card-green', 'East West Rail — Phase 4', 'data', PROJ_GREEN, [
          { label: 'Status', value: 'ON TRACK', status: 'good' },
          { label: 'Budget', value: '£8.2M / £9.1M', status: 'good' },
          { label: 'Progress', value: '72%', status: 'good' },
          { label: 'RAG', value: 'GREEN', status: 'good' },
        ]),
        hudEvent('project', 'EAST WEST RAIL — PH4', 'On track. 72% complete. Budget within tolerance. No critical actions.', 'success'),
        hudTeam([
          { name: 'East West Rail — Ph4', status: 'on-track', destination: '72% complete' },
          { name: 'Newgate Lane — Deadham', status: 'monitoring', destination: 'Under review' },
        ]),
      ]),

      removePanel('card-green'),

      // Red project — Newgate Lane Deadham (IN EXCEPTION)
      beat(7000, [
        dronePushIn(PROJ_RED, { bearing: 30, zoom: 15.5, duration: 7000 }),
        { type: 'model-status', duration: 0, params: { coordinates: gp(14, -16), infraId: 'depot-east', color: '#ef4444', emissive: 0.8 } },
        panel('card-red', 'Newgate Lane — Deadham', 'data', PROJ_RED, [
          { label: 'Status', value: 'IN EXCEPTION', status: 'critical' },
          { label: 'Budget', value: '£5.7M / £4.2M', status: 'critical' },
          { label: 'Progress', value: '31%', status: 'warning' },
          { label: 'RAG', value: 'RED', status: 'critical' },
        ]),
        hudEvent('alert', 'EXCEPTION — NEWGATE LANE', 'Budget overrun: £1.5M over forecast. Schedule slipped 119 days. Intervention required.', 'critical'),
        { type: 'hud-metric', duration: 0, params: { id: 'variance', kind: 'counter', label: 'Variance', value: -1500, unit: '£K', severity: 'critical' } },
      ]),

      // Portfolio constellation updates — Newgate goes RED
      { type: 'clear-effect', duration: 0, params: { id: 'portfolio' } } as ScenarioStep,
      { type: 'multi-site-rag', duration: 0, params: {
        id: 'portfolio',
        sites: [
          { coordinates: gp(11, 22), status: 'green' },
          { coordinates: gp(14, -16), status: 'red' },
          { coordinates: gp(-25, -12), status: 'green' },
          { coordinates: gp(22, 22), status: 'green' },
          { coordinates: gp(26, -24), status: 'amber' },
          { coordinates: gp(-22, -22), status: 'green' },
          { coordinates: gp(-26, 6), status: 'green' },
          { coordinates: gp(-18, 28), status: 'amber' },
        ],
        linkTo: { coordinates: gp(-2, -4), infraId: 'control-centre' },
      }} as ScenarioStep,

      // Budget flow to red project turns red
      { type: 'clear-effect', duration: 0, params: { id: 'budget-flow-red' } } as ScenarioStep,
      { type: 'flow-pulse', duration: 0, params: {
        id: 'budget-flow-red', path: BUDGET_TO_RED, color: '#ef4444', speed: 1.5,
      }} as ScenarioStep,

      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(14, -16), infraId: 'depot-east', zoom: 15.5, pitch: 55, bearing: 0 } },
        { type: 'color-grade', duration: 0, params: { tint: '#ef4444', intensity: 0.05, vignette: 0.2 } },
        notification('EXCEPTION FLAGGED', 'AI-triggered exception. Budget +£1.5M over. Schedule -119 days. Confidence dropped.', 'critical', PACING.BEAT),
        { type: 'hud-metric', duration: 0, params: { id: 'confidence', kind: 'counter', label: 'Confidence', value: 62, unit: '%', severity: 'critical' } },
      ]),

      removePanel('card-red'),

      productScreen('Project Cards — Portfolio View', {
        image: assetUrl('/screens/capd-project-cards.png'),
        device: 'desktop',
        subtitle: 'Project Manager — all projects with phase filters and progress',
        role: 'Programme Manager',
        url: 'app.skewb.io/capital-delivery/projects',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 3: SCHEDULE VIEW — Delayed milestones, forecast slippage (55–90s)
    // Camera orbits the red project. Panel shows milestone timeline with
    // delays. Critical actions flagged. Recovery actions identified.
    // ═══════════════════════════════════════════════════════════════════════
    act('Schedule View', [
      beat(PACING.PUSH_IN, [
        dronePushIn(PROJ_RED, { bearing: -40, zoom: 16 }),
        hudEvent('schedule', 'SCHEDULE ANALYSIS', 'Project timeline: 5 of 8 milestones delayed. Feasibility/Conceptual overran by 84 days.', 'critical'),
        panel('schedule', 'Schedule — Newgate Lane', 'data', PROJ_RED, [
          { label: 'Work Request', value: 'Complete', status: 'good' },
          { label: 'Feasibility', value: '84 days LATE', status: 'critical' },
          { label: 'Detailed Design', value: '35 days LATE', status: 'critical' },
          { label: 'Construction', value: 'NOT STARTED', status: 'warning' },
        ]),
      ]),

      beat(PACING.ORBIT, [
        orbit180(PROJ_RED, { startBearing: -40, arc: 140, zoom: 16, pitch: 60, duration: PACING.ORBIT }),
        hudEvent('actions', 'CRITICAL ACTIONS', '3 critical actions open. Cost RAG: Red. Time RAG: Red. Quality RAG: Amber.', 'critical'),
        hudTeam([
          { name: 'Initiation', status: 'complete', destination: 'Not Started → Done' },
          { name: 'Estimate Validation', status: 'complete', destination: 'Not Started → Done' },
          { name: 'Feasibility/Conceptual', status: 'on-site', destination: '84 days over' },
          { name: 'Detailed Design', status: 'on-site', destination: '35 days over' },
        ]),
      ]),

      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(14, -16), infraId: 'depot-east', zoom: 16, pitch: 60, bearing: 60 } },
        updatePanel('schedule', [
          { label: 'Forecast Slippage', value: '119 days', status: 'critical' },
          { label: 'Recovery Actions', value: '3 identified', status: 'warning' },
          { label: 'Reforecast Due', value: 'Immediate', status: 'critical' },
          { label: 'Gate Hold', value: 'Pre-Construction', status: 'critical' },
        ]),
        notification('RECOVERY PLAN REQUIRED', 'AI recommends: reforecast budget, compress design phase, parallel procurement.', 'warning', PACING.BEAT),
      ]),

      removePanel('schedule'),

      productScreen('Project Dashboard — Schedule', {
        image: assetUrl('/screens/capd-project-schedule.png'),
        device: 'desktop',
        subtitle: 'Project schedule — Gantt view with milestones and task dependencies',
        role: 'Project Manager',
        url: 'app.skewb.io/capital-delivery/project/newgate-lane/schedule',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 4: INVESTMENT OVERSIGHT — Budget, actuals, variance (90–125s)
    // Pull back to show the financial picture. Budget burndown fills red.
    // AI reforecasts. Recovery actions activate. Variance starts closing.
    // ═══════════════════════════════════════════════════════════════════════
    act('Investment Oversight', [
      beat(PACING.PUSH_IN, [
        craneReveal(HUB, { zoom: 14, bearing: -10 }),
        hudEvent('finance', 'INVESTMENT OVERSIGHT', 'Total Planned: £42M. Committed: £28M. Raised: £12M. Variance: -£1.5M on Newgate.', 'warning'),
        panel('investment', 'Investment Oversight — Portfolio', 'data', HUB, [
          { label: 'Total Planned', value: '£42M', status: 'good' },
          { label: 'Committed', value: '£28M' },
          { label: 'Actuals', value: '£12M' },
          { label: 'Variance', value: '-£1.5M', status: 'critical' },
        ]),
        { type: 'hud-metric', duration: 0, params: { id: 'budget', kind: 'burndown', label: 'Programme Spend', value: 15, target: 42, unit: '£M', severity: 'warning' } },
      ]),

      // AI reforecast activates
      beat(8000, [
        orbit180(HUB, { startBearing: -10, arc: 90, zoom: 14, pitch: 45, duration: 8000 }),
        hudEvent('ai', 'AI REFORECAST', 'Machine learning model analysing 3 recovery scenarios. Optimal path identified.', 'info'),
        notification('AI INTERVENTION', 'Reforecast model running. Compressing design phase + parallel procurement recommended.', 'info', 5000),
        { type: 'hud-metric', duration: 0, params: { id: 'confidence', kind: 'counter', label: 'Confidence', value: 68, unit: '%', severity: 'warning' } },
      ]),

      // Recovery actions close — variance reducing
      beat(7000, [
        { type: 'camera', duration: 7000, params: { center: gp(6, -10), zoom: 14.5, pitch: 48, bearing: 20 } },
        updatePanel('investment', [
          { label: 'Reforecast', value: '£4.8M (was £5.7M)', status: 'warning' },
          { label: 'Savings Found', value: '£0.9M', status: 'good' },
          { label: 'Revised Variance', value: '-£0.6M', status: 'warning' },
          { label: 'Risk Reduction', value: '£880K', status: 'good' },
        ]),
        hudEvent('recovery', 'RECOVERY ACTIVATED', 'Design compression: 22 days saved. Parallel procurement: £0.9M saved. Revised RAG: Amber.', 'success'),
        { type: 'hud-metric', duration: 0, params: { id: 'variance', kind: 'counter', label: 'Variance', value: -600, unit: '£K', severity: 'warning' } },
      ]),

      removePanel('investment'),

      productScreen('Investment Oversight Dashboard', {
        image: assetUrl('/screens/capd-investment-oversight.png'),
        device: 'desktop',
        subtitle: 'Investment performance overview — planned vs committed vs raised vs actual',
        role: 'Commercial Controller',
        url: 'app.skewb.io/capital-delivery/investment-oversight',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 5: RESOLUTION — Portfolio confidence restored (125–165s)
    // Red project shifts to AMBER. Constellation updates. Budget flow heals.
    // Confidence climbs. Colour grade resolves to green. Pull back.
    // ═══════════════════════════════════════════════════════════════════════
    act('Confidence Restored', [
      { type: 'clear-effect', duration: 0, params: { id: 'budget-flow-red' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'budget-flow-green' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'portfolio' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'budget' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'variance' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'confidence' } } as ScenarioStep,

      // Newgate shifts to AMBER — recovery in progress
      { type: 'model-status', duration: 0, params: { coordinates: gp(14, -16), infraId: 'depot-east', color: '#f59e0b', emissive: 0.5 } } as ScenarioStep,

      beat(PACING.PUSH_IN, [
        dronePushIn(PROJ_RED, { bearing: 0, zoom: 15.5 }),
        hudEvent('recovery', 'NEWGATE — RECOVERING', 'Actions closing. Revised timeline accepted. Gate hold released. RAG: Amber.', 'success'),
        panel('recovery', 'Newgate Lane — Recovery', 'data', PROJ_RED, [
          { label: 'Status', value: 'RECOVERING', status: 'warning' },
          { label: 'Revised Budget', value: '£4.8M', status: 'warning' },
          { label: 'Actions Open', value: '1 (was 3)', status: 'warning' },
          { label: 'Gate Hold', value: 'RELEASED', status: 'good' },
        ]),
      ]),

      removePanel('recovery'),

      // Pull back to full portfolio — all green/amber, none red
      { type: 'color-grade', duration: 0, params: { tint: '#10b981', intensity: 0.05, vignette: 0.1 } } as ScenarioStep,

      { type: 'multi-site-rag', duration: 0, params: {
        id: 'portfolio-resolved',
        sites: [
          { coordinates: gp(11, 22), status: 'green' },
          { coordinates: gp(14, -16), status: 'amber' },
          { coordinates: gp(-25, -12), status: 'green' },
          { coordinates: gp(22, 22), status: 'green' },
          { coordinates: gp(26, -24), status: 'green' },
          { coordinates: gp(-22, -22), status: 'green' },
          { coordinates: gp(-26, 6), status: 'green' },
          { coordinates: gp(-18, 28), status: 'green' },
        ],
        linkTo: { coordinates: gp(-2, -4), infraId: 'control-centre' },
      }} as ScenarioStep,

      // Budget flows restored green
      { type: 'flow-pulse', duration: 0, params: {
        id: 'budget-final-green', path: BUDGET_TO_GREEN, color: '#10b981', speed: 0.8,
      }} as ScenarioStep,
      { type: 'flow-pulse', duration: 0, params: {
        id: 'budget-final-red', path: BUDGET_TO_RED, color: '#f59e0b', speed: 0.8,
      }} as ScenarioStep,

      dimEnd,

      beat(8000, [
        craneReveal(HUB, { zoom: 13.5, bearing: -20, duration: 8000 }),
        { type: 'model-status', duration: 0, params: { coordinates: gp(-2, -4), infraId: 'control-centre', color: '#10b981', emissive: 0.6 } },
        hudEvent('complete', 'PORTFOLIO CONFIDENCE RESTORED', 'Zero RED projects. Programme on track. AI intervention saved £0.9M and 22 days.', 'success'),
        { type: 'hud-metric', duration: 0, params: { id: 'confidence', kind: 'counter', label: 'Confidence', value: 91, unit: '%', severity: 'success' } },
        { type: 'hud-metric', duration: 0, params: { id: 'budget', kind: 'burndown', label: 'Programme Spend', value: 15, target: 42, unit: '£M', severity: 'success' } },
        hudRegion([
          { name: 'On Track', status: 'green', properties: 591 },
          { name: 'At Risk', status: 'amber', properties: 34 },
          { name: 'In Exception', status: 'red', properties: 0 },
        ]),
        notification('INTERVENTION CLOSED', 'Newgate Lane recovering. Portfolio confidence 91%. Zero exceptions remaining.', 'success', 5000),
      ]),

      { type: 'clear-effect', duration: 0, params: { id: 'portfolio-resolved' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'budget-final-green' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'budget-final-red' } } as ScenarioStep,

      productScreen('Capital Delivery Live — Resolved', {
        image: assetUrl('/screens/capd-portfolio-resolved.png'),
        device: 'desktop',
        subtitle: 'Pinned projects — post-intervention view with recovery confirmed',
        role: 'Programme Director',
        url: 'app.skewb.io/capital-delivery/live',
      }),

      { type: 'color-grade', duration: 0, params: { tint: '#fbbf24', intensity: 0.04, vignette: 0.1 } } as ScenarioStep,

      beat(PACING.PULLBACK, [
        majesticPullback({ bearing: -15 }),
        notification('CAPITAL DELIVERY', '£42M programme — 625 projects — AI-driven intervention — zero exceptions.', 'success'),
      ]),

      { type: 'color-grade', duration: 0, params: { tint: 'transparent', intensity: 0, vignette: 0 } } as ScenarioStep,
      hold(PACING.HOLD),
    ]),
  ],
);
